import { useMutation } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import {
    faBullhorn,
    faBullseye,
    faChartColumn,
    faFill,
    faPaperPlaneTop,
    faTableList,
} from '@fortawesome/pro-regular-svg-icons';
import { activateEngage } from 'api/engage';
import Content from 'assets/js/components/Content';
import * as EngageFeature from 'assets/js/pages/engage/EngageFeature';
import { useEngageConfig } from 'assets/js/pages/engage/useEngageConfiguration';
import * as SubNav from 'components/sub-nav';
import { EngageSubNavTabs } from 'components/sub-nav/EngageSubNavTabs';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInvalidateUserAndAccount } from 'customHooks/useInvalidateUserAndAccount';

const SplashScreen = () => {
    const toast = useToast();
    const { mutate: invalidateUserData } = useInvalidateUserAndAccount();
    const { isAccountVerified, accountId } = useCurrentAccount();
    const { engageConfig } = useEngageConfig();

    const { mutate, isPending: isLoading } = useMutation({
        mutationFn: () => activateEngage(accountId, isAccountVerified ? 'active' : 'pending'),
        onSuccess: () => invalidateUserData(),
        onError: () => {
            toast.error(
                <>
                    <span className="font-medium">Whoops.</span> Something went wrong. Please try
                    again.
                </>,
            );
        },
    });

    const engageDenied = engageConfig.status === 'denied';
    const engagePending = engageConfig.status === 'pending';

    return (
        <Content className="flex flex-col gap-y-4">
            <SubNav.Root>
                <SubNav.Header>
                    <SubNav.Title>Engage</SubNav.Title>
                </SubNav.Header>
                <EngageSubNavTabs />
            </SubNav.Root>
            <div className="flex flex-col gap-8">
                {engageDenied && (
                    <AlertNotification.Root type="error">
                        <AlertNotification.Icon />
                        <AlertNotification.Content>
                            <AlertNotification.Title>Access denied</AlertNotification.Title>
                            <div>
                                Your account needs additional verification in order to use Engage.
                                Please contact{' '}
                                <a href="mailto:support@givebutter.com">support@givebutter.com</a>{' '}
                                for more information.
                            </div>
                        </AlertNotification.Content>
                        <AlertNotification.Close />
                    </AlertNotification.Root>
                )}
                {engagePending && (
                    <AlertNotification.Root type="info">
                        <AlertNotification.Icon />
                        <AlertNotification.Content>
                            <AlertNotification.Title>Request pending</AlertNotification.Title>
                            <div>
                                Your account is being reviewed by our team before you can start
                                using Engage. Please contact{' '}
                                <a href="mailto:support@givebutter.com">support@givebutter.com</a>{' '}
                                for more information.
                            </div>
                        </AlertNotification.Content>
                        <AlertNotification.Close />
                    </AlertNotification.Root>
                )}
                <div className="flex flex-col items-center gap-6 md:flex-row">
                    <div className="flex flex-1 flex-col gap-7">
                        <h1 className="text-4xl font-medium">
                            Send personalized emails, texts, videos, and more
                        </h1>

                        <div className="hidden text-xl md:block">
                            Engage your community like never before. This is donor stewardship, done
                            right.
                        </div>

                        <div>
                            <Button
                                variant="solid"
                                disabled={engagePending || engageDenied || isLoading}
                                onClick={() => mutate()}
                            >
                                {isLoading && <Loader variant={'static'} />}
                                {isAccountVerified ? 'Get started' : 'Request access'}
                            </Button>
                        </div>
                    </div>
                    <div>
                        <img
                            alt="Engage splash"
                            className="w-96"
                            src={`/images/illustrations/engage-splash.png`}
                        />
                    </div>
                </div>

                <h1 className="text-3xl font-medium">Features</h1>

                <div className="grid gap-5 lg:grid-cols-3">
                    <EngageFeature.Root>
                        <EngageFeature.Header>
                            <EngageFeature.Icon icon={faPaperPlaneTop} rotation={270} />
                            <EngageFeature.Title>Targeted Emails</EngageFeature.Title>
                        </EngageFeature.Header>
                        <EngageFeature.Description>
                            Go beyond your campaigns and filter all your contacts, ticket holders,
                            and campaign supporters by giving history, campaign activity, location,
                            and more to create highly targeted emails.
                        </EngageFeature.Description>
                    </EngageFeature.Root>
                    <EngageFeature.Root>
                        <EngageFeature.Header>
                            <EngageFeature.Icon icon={faTableList} />
                            <EngageFeature.Title>Email Editor</EngageFeature.Title>
                        </EngageFeature.Header>
                        <EngageFeature.Description>
                            Say goodbye to complicated email editors. We've streamlined the process
                            by taking everything you love about our campaign story editor and
                            created our new super easy-to-use email editor.
                        </EngageFeature.Description>
                    </EngageFeature.Root>
                    <EngageFeature.Root>
                        <EngageFeature.Header>
                            <EngageFeature.Icon icon={faFill} />
                            <EngageFeature.Title>Branding & Domains</EngageFeature.Title>
                        </EngageFeature.Header>
                        <EngageFeature.Description>
                            Set your logo, brand color, and custom domain once, and then start
                            sending your on-brand emails from your email address.
                        </EngageFeature.Description>
                    </EngageFeature.Root>
                    <EngageFeature.Root>
                        <EngageFeature.Header>
                            <EngageFeature.Icon icon={faChartColumn} />
                            <EngageFeature.Title>Message ROI</EngageFeature.Title>
                        </EngageFeature.Header>
                        <EngageFeature.Description>
                            See the direct impact of your messages by tracking statistics like
                            opens, bounces, and click-through to optimize your messages further.
                        </EngageFeature.Description>
                    </EngageFeature.Root>
                    <EngageFeature.Root>
                        <EngageFeature.Header>
                            <EngageFeature.Icon icon={faBullseye} />
                            <EngageFeature.Title>Track Integration</EngageFeature.Title>
                        </EngageFeature.Header>
                        <EngageFeature.Description>
                            You can now automatically track your contact's message engagement
                            (opens, clicks) - zero integration set-up is necessary.
                        </EngageFeature.Description>
                    </EngageFeature.Root>
                    <EngageFeature.Root>
                        <EngageFeature.Header>
                            <EngageFeature.Icon icon={faBullhorn} />
                            <EngageFeature.Title>Campaign Updates</EngageFeature.Title>
                        </EngageFeature.Header>
                        <EngageFeature.Description>
                            Keep your campaign supporters in the loop by sending email updates, as
                            well as posting and pinning the same updates to your campaign's
                            supporter feed.
                        </EngageFeature.Description>
                    </EngageFeature.Root>
                </div>
            </div>
        </Content>
    );
};

export { SplashScreen };
